import { ReactNode, useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useRunCannibalizationMutation,
  useRunContentGapMutation,
  useRunLinkStructureMutation
} from '@/common/api/modules/article'
import { useRunCitationAnalysisApi } from '@/common/api/modules/citation'
import { useRunCompetitorApi } from '@/common/api/modules/competitor'
import { useRunCompetitorSerpApi } from '@/common/api/modules/competitorSerp'
import { useRunLlmoAnalysisApi } from '@/common/api/modules/llmo'
import { useAnalysisTypesApi } from '@/common/api/modules/mediaAnalysis'
import { useAnalysisTargetListApi } from '@/common/api/modules/mediaAnalysisTarget'
import { AnalysisTypeDef } from '@/common/api/params/mediaAnalysis'
import { mediaAnalysisImage } from '@/common/app/modules/media/mediaAnalysis'
import {
  analysisTargetPath,
  mediaAnalysisHistoryPath,
  mediaAnalysisResultPath
} from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { MediaAnalysisType, isLLMOAnalysisType } from '@/common/model/media/mediaAnalysis'
import { MediaAnalysisTarget } from '@/common/model/media/mediaAnalysisTarget'

import AiButton from '@/components/view/atoms/button/AiButton'
import Button from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import ListFormLoading from '@/components/view/molecules/loading/ListFormLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import MediaAnalysisHelpModalProvider, {
  MediaAnalysisHelpModalContext
} from '@/components/container/modal/mediaAnalysis/MediaAnalysisHelpModalProvider'
import AnalysisTargetSummary from '@/components/container/modules/media/analysisTarget/AnalysisTargetSummary'

// 分析対象設定カードの1行（ラベル128px + 値）
const TargetInfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className='flex items-baseline gap-4'>
    <div className='w-32 shrink-0'>
      <Text size={12} weight='bold'>
        {label}
      </Text>
    </div>
    <div className='flex min-w-0 flex-1 flex-col gap-2'>{children}</div>
  </div>
)

// 「含む : ○○」のようなキー・値の1行
const KeyValueLine = ({ label, value }: { label: string; value: string | null }) => (
  <div className='flex items-center gap-3'>
    <div className='w-12'>
      <Text size={12} color={TextColor.SECONDARY}>
        {label}
      </Text>
    </div>
    <Text size={12} color={TextColor.SECONDARY}>
      :
    </Text>
    <Text size={14}>{value || '-'}</Text>
  </div>
)

// 分析対象に不足があり実行不可の場合の理由（nullは実行可）
const missingReason = (typeKey: string, target: MediaAnalysisTarget | undefined): string | null => {
  if (!target) return '分析対象を選択してください'
  switch (typeKey) {
    case MediaAnalysisType.COMPETITOR_SERP:
      return target.keyword_include ? null : '分析対象に「キーワード（含む）」を設定してください'
    case MediaAnalysisType.CITATION:
      return target.own_brand && target.own_url
        ? null
        : '分析対象に自社ブランド名・URLを設定してください'
    case MediaAnalysisType.SHARE_OF_VOICE:
    case MediaAnalysisType.SENTIMENT:
    case MediaAnalysisType.QUERY_FANOUT:
      return target.own_brand && target.own_url && target.category
        ? null
        : '分析対象に自社ブランド名・URL・カテゴリを設定してください'
    default:
      return null
  }
}

interface CardProps {
  siteId: number
  type: AnalysisTypeDef
  handleExecute: (typeKey: MediaAnalysisType) => void
  isRunning: boolean
  target: MediaAnalysisTarget | undefined
}

const MediaAnalysisTypeCard = ({ siteId, type, handleExecute, isRunning, target }: CardProps) => {
  const reason = missingReason(type.key, target)

  return (
    <Area key={type.key} bottomMargin={0}>
      <AreaContent>
        <ItemAlign direction='vertical' align='center' gapY={8}>
          <img src={mediaAnalysisImage(type.key)} width={120} height={80} alt={type.label} />
          <Text size={16} weight='bold'>
            {type.label}
          </Text>
          <Text size={12} tag='p'>
            {type.description}
          </Text>
        </ItemAlign>
        <div className='flex flex-col items-center gap-2 pt-3'>
          <div>
            <AiButton
              size='large'
              disabled={!!reason}
              loading={isRunning}
              onClick={() => handleExecute(type.key)}
            >
              {isRunning ? '分析しています' : '分析を実行する'}
            </AiButton>
          </div>
          {reason && (
            <Text size={10} color={TextColor.CAUTION} align='center'>
              {reason}
            </Text>
          )}
          <TextButton link={`${mediaAnalysisHistoryPath(siteId)}?type=${type.key}`} arrow>
            過去の結果を見る
          </TextButton>
        </div>
      </AreaContent>
    </Area>
  )
}

// サイト現状分析（分析対象選択 + 種別ごとの実行カード）
const MediaAnalysisPageContent = () => {
  const siteId = useSiteId()
  const navigate = useNavigate()

  const { data: targets, isLoading: isTargetLoading } = useAnalysisTargetListApi({
    site_id: siteId
  })
  const { data: types, isLoading: isTypeLoading } = useAnalysisTypesApi({ site_id: siteId })

  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>(undefined)
  const target = (targets ?? []).find((t) => t.id === selectedTargetId) ?? targets?.[0]

  // 完了時にナビゲートすべきか（すべて実行中は個別のナビゲートを抑止する）。
  // デフォルトはtrue、実行の直前にhandleExecuteが上書きする
  const navigateFlagsRef = useRef<Partial<Record<string, boolean>>>({})

  const goToResult = (typeKey: MediaAnalysisType, historyId: number | null | undefined) => {
    const shouldNavigate = navigateFlagsRef.current[typeKey] !== false
    navigateFlagsRef.current[typeKey] = true
    if (!shouldNavigate || historyId == null) return
    navigate(mediaAnalysisResultPath(siteId, typeKey, historyId))
  }

  // ---- 各分析の実行（バックグラウンド実行）: レコード作成→即座にidを返し、詳細画面へ即遷移 ----
  // 分析はバックグラウンドで進み、詳細画面で status をポーリングして AiLoading を表示する
  const { trigger: runCompetitor, isMutating: competitorRunning } = useRunCompetitorApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.COMPETITOR, res.id)
  })
  const { trigger: runSerp, isMutating: serpRunning } = useRunCompetitorSerpApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.COMPETITOR_SERP, res.id)
  })
  const { trigger: runContentGap, isMutating: contentGapRunning } = useRunContentGapMutation({
    onSuccess: (res) => goToResult(MediaAnalysisType.CONTENT_GAP, res.id)
  })
  const { trigger: runCannibalization, isMutating: cannibalizationRunning } =
    useRunCannibalizationMutation({
      onSuccess: (res) => goToResult(MediaAnalysisType.CANNIBALIZATION, res.id)
    })
  const { trigger: runLinkStructure, isMutating: linkStructureRunning } =
    useRunLinkStructureMutation({
      onSuccess: (res) => goToResult(MediaAnalysisType.LINK_STRUCTURE, res.id)
    })

  // ---- GEO/LLMO（バックグラウンド実行）----
  // シェア・オブ・ボイス／センチメント／クエリファンアウトは同一エンドポイント（llmo/run）のため、
  // 種別ごとの実行中表示を保てるようフックを種別ごとに呼び分ける
  const { trigger: runCitation, isMutating: citationRunning } = useRunCitationAnalysisApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.CITATION, res.id)
  })
  const { trigger: runSov, isMutating: sovRunning } = useRunLlmoAnalysisApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.SHARE_OF_VOICE, res.id)
  })
  const { trigger: runSentiment, isMutating: sentimentRunning } = useRunLlmoAnalysisApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.SENTIMENT, res.id)
  })
  const { trigger: runQueryFanout, isMutating: queryFanoutRunning } = useRunLlmoAnalysisApi({
    onSuccess: (res) => goToResult(MediaAnalysisType.QUERY_FANOUT, res.id)
  })

  // silent=trueの場合、完了してもその分析結果詳細への自動遷移を行わない（「すべての分析を実行する」用）
  const handleExecute = (typeKey: MediaAnalysisType, options?: { silent?: boolean }) => {
    if (!target) return
    navigateFlagsRef.current[typeKey] = !options?.silent
    switch (typeKey) {
      case MediaAnalysisType.COMPETITOR:
        runCompetitor({ site_id: siteId, media_analysis_target_id: target.id }).catch(
          () => undefined
        )
        return
      case MediaAnalysisType.COMPETITOR_SERP:
        runSerp({ site_id: siteId, media_analysis_target_id: target.id }).catch(() => undefined)
        return
      case MediaAnalysisType.CONTENT_GAP:
        runContentGap({ site_id: siteId }).catch(() => undefined)
        return
      case MediaAnalysisType.CANNIBALIZATION:
        runCannibalization({ site_id: siteId }).catch(() => undefined)
        return
      case MediaAnalysisType.LINK_STRUCTURE:
        runLinkStructure({ site_id: siteId }).catch(() => undefined)
        return
      case MediaAnalysisType.CITATION:
        runCitation({ site_id: siteId, media_analysis_target_id: target.id }).catch(() => undefined)
        return
      case MediaAnalysisType.SHARE_OF_VOICE:
        runSov({
          site_id: siteId,
          media_analysis_target_id: target.id,
          analysis_type: 'share_of_voice'
        }).catch(() => undefined)
        return
      case MediaAnalysisType.SENTIMENT:
        runSentiment({
          site_id: siteId,
          media_analysis_target_id: target.id,
          analysis_type: 'sentiment'
        }).catch(() => undefined)
        return
      case MediaAnalysisType.QUERY_FANOUT:
        runQueryFanout({
          site_id: siteId,
          media_analysis_target_id: target.id,
          analysis_type: 'query_fanout'
        }).catch(() => undefined)
        return
    }
  }

  const isRunning = (typeKey: MediaAnalysisType): boolean => {
    switch (typeKey) {
      case MediaAnalysisType.COMPETITOR:
        return competitorRunning
      case MediaAnalysisType.CONTENT_GAP:
        return contentGapRunning
      case MediaAnalysisType.CANNIBALIZATION:
        return cannibalizationRunning
      case MediaAnalysisType.LINK_STRUCTURE:
        return linkStructureRunning
      case MediaAnalysisType.COMPETITOR_SERP:
        return serpRunning
      case MediaAnalysisType.CITATION:
        return citationRunning
      case MediaAnalysisType.SHARE_OF_VOICE:
        return sovRunning
      case MediaAnalysisType.SENTIMENT:
        return sentimentRunning
      case MediaAnalysisType.QUERY_FANOUT:
        return queryFanoutRunning
      default:
        return false
    }
  }

  const normalTypes = types?.filter((t) => t.enabled && !isLLMOAnalysisType(t.key)) ?? []
  const llmoTypes = types?.filter((t) => t.enabled && isLLMOAnalysisType(t.key)) ?? []
  const allTypes = [...normalTypes, ...llmoTypes]

  // 実行可能（分析対象が条件を満たし、かつ現在実行中でない）な種別のみをまとめて実行する
  const runnableTypes = allTypes.filter((t) => !missingReason(t.key, target) && !isRunning(t.key))
  const anyRunning = allTypes.some((t) => isRunning(t.key))

  const handleExecuteAll = () => {
    runnableTypes.forEach((t) => handleExecute(t.key, { silent: true }))
  }

  const { showModal } = useContext(MediaAnalysisHelpModalContext)

  return (
    <Main type={MainLayoutType.SMALL}>
      <PageTitle
        title='サイト現状分析'
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
        rightNode={
          <AiButton
            size='large'
            disabled={!target || runnableTypes.length === 0}
            loading={anyRunning}
            onClick={handleExecuteAll}
          >
            {anyRunning ? '実行しています' : 'すべての分析を実行する'}
          </AiButton>
        }
        description='分析対象と条件を設定し、各分析をワンタッチで実行します。'
      />

      <Area bottomMargin={16}>
        <Heading>分析対象設定</Heading>
        <AreaContent>
          {targets && targets.length === 0 ? (
            isTargetLoading ? (
              <ListFormLoading />
            ) : (
              <>
                <Text size={12} color={TextColor.SECONDARY}>
                  分析対象が登録されていません。
                </Text>
                <Button link={analysisTargetPath(siteId)}>分析対象を登録する</Button>
              </>
            )
          ) : (
            <FormRow>
              <FormLabel>分析対象サイト名</FormLabel>
              <FormRow direction='vertical' gap={8}>
                <FormRow align='center' gap={8}>
                  <Selector
                    name='media_analysis_target_id'
                    size={SelectorSize.AUTO}
                    choices={(targets ?? []).map((t) => ({ value: t.id, name: t.name }))}
                    value={target?.id ?? ''}
                    onChange={(e) => setSelectedTargetId(Number(e.target.value))}
                  />
                  <TextButton link={analysisTargetPath(siteId)} newTab>
                    分析対象管理
                  </TextButton>
                </FormRow>
                {target && <AnalysisTargetSummary target={target} />}
              </FormRow>
            </FormRow>
          )}
        </AreaContent>
      </Area>
      {isTypeLoading && <ListFormLoading />}
      <SummaryHeading
        subNode={
          <TextButton onClick={() => showModal({ isGeoLlmo: false })} nowrap>
            <Icon type={IconType.HELP} color={IconColor.LINK} line />
            SEO分析について
          </TextButton>
        }
      >
        SEO分析
      </SummaryHeading>
      <div className='grid grid-cols-1 gap-4 pb-6 md:grid-cols-2 lg:grid-cols-3'>
        {normalTypes.map((t: AnalysisTypeDef) => (
          <MediaAnalysisTypeCard
            key={t.key}
            siteId={siteId}
            type={t}
            handleExecute={handleExecute}
            isRunning={isRunning(t.key)}
            target={target}
          />
        ))}
      </div>
      {llmoTypes.length > 0 && (
        <>
          <SummaryHeading
            subNode={
              <TextButton onClick={() => showModal({ isGeoLlmo: true })} nowrap>
                <Icon type={IconType.HELP} color={IconColor.LINK} line />
                GEO/LLMO分析について
              </TextButton>
            }
          >
            GEO/LLMO分析
          </SummaryHeading>
          <div className='grid grid-cols-1 gap-4 pb-6 md:grid-cols-2 lg:grid-cols-3'>
            {llmoTypes.map((t: AnalysisTypeDef) => (
              <MediaAnalysisTypeCard
                key={t.key}
                siteId={siteId}
                type={t}
                handleExecute={handleExecute}
                isRunning={isRunning(t.key)}
                target={target}
              />
            ))}
          </div>
        </>
      )}
    </Main>
  )
}

const MediaAnalysisPage = () => (
  <MediaAnalysisHelpModalProvider>
    <MediaAnalysisPageContent />
  </MediaAnalysisHelpModalProvider>
)

export default MediaAnalysisPage
